import type {
  CargoPaymentMethod,
  PatchCargoStatusBody,
  ReportCargoDirection,
  ReportCargoStatus,
} from '../types/reportGroupedCargos'
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AxiosResponse } from 'axios'
import axiosInstance from '../../utils/axiosInstance'
import { toast } from 'react-toastify'
import { URL } from '../../constants/url'
import { RootState } from '../store'
import { MyKnownError } from '../types/common'
import { Client } from './clients'
import {
  fetchAllReportGroupedCargos,
  fetchCurrentReport,
  fetchReportGroupedCargos,
  Report,
} from './reports'
import { fetchInvoices, setCurrentInvoice } from './invoices'
import { canCurrentUserDeleteCargo } from '../../utils/cargoDeletePermission'
import { getFiltersFromSessionStorage } from '../../utils/filterUtils'
import { getRole } from '../../utils/getRole'
import moment from 'moment'

function formatCargoReadyAtForApi(readyAt?: string): string {
  const trimmed = readyAt?.trim() ?? ''
  if (!trimmed) return ''
  const m = moment(trimmed, [
    'YYYY-MM-DDTHH:mm',
    'YYYY-MM-DD HH:mm',
    'YYYY-MM-DD HH:mm:ss',
  ])
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : trimmed
}

export interface Cargo {
  id: number
  report_id: number
  invoice_id: number | null
  from_id: number
  from: Client
  to: Client
  to_id: number
  payer: number
  description: string
  volume: number
  weight: number
  amount: number
  currency: string
  report: Report
  has_tn: boolean
  tn_number: string
  third_id?: number // Добавляем поле для третьего лица
  client?: Client // Добавляем поле client согласно JSON
  payment_method?: CargoPaymentMethod
  /** Оплачен (задаётся вручную владельцем/админом) */
  is_paid?: boolean | number | string
  status?: ReportCargoStatus
  is_active?: boolean
  images?: Array<{
    id: number
    image: {
      lg: string
      md: string
      sm: string
    }
    created_by?: number
  }>
  /** Автор записи груза (для водителя: удаление только своих) */
  created_by?: number | null
  readiness_at?: string | null
  can_be_picked_up?: boolean
}

export interface CargosResponse {
  [key: string]: Cargo[]
}

export type ReportCargoDeleteDialog =
  | { type: 'shipment'; cargo?: Cargo; direction?: ReportCargoDirection }
  | {
      type: 'delivery'
      senderRowKey: string
      cargoIds: number[]
      direction?: ReportCargoDirection
    }

export type ReportCargoTabPatch =
  | { kind: 'removeCargo'; cargoId: number }
  | { kind: 'removeDelivery'; senderRowKey: string }

export type CargoModalReportTabConstraints = {
  lockPl: boolean
  lockSender: boolean
  direction?: ReportCargoDirection
  listKind?: 'loading' | 'unloading'
}

export interface InitState {
  cargos: CargosResponse | null
  currentCargo: Cargo | null
  currentCargoGroup: Cargo[] | null
  selectedCargosForDeletion: number[]
  reportCargoDeleteDialog: ReportCargoDeleteDialog | null
  reportCargoTabPatch: ReportCargoTabPatch | null
  isPending: boolean
  isPendingGetCargos: boolean
  isPendingAddCargos: boolean
  isPendingUpdateCargos: boolean
  isPendingDeleteCargos: boolean
  isPendingDeleteCargosImage: boolean
  isPendingDeleteManyCargos: boolean
  isVisibleModalCargo: boolean
  isVisibleDeleteModalCargo: boolean
  isVisibleDeleteManyModalCargo: boolean
  isOnlyCargo: boolean
  // внутренее взаимодействие
  isVisibleSideModal: boolean
  isVisibleContentDeliveries: boolean
  isDisabledEdit: boolean
  currentCargoGroupIdClient: number | null
  /** Одноразово подставить путевой лист при создании груза из вкладки путевого */
  newCargoReportIdPrefill: number | null
  /** Режим формы при создании груза из путевого (блокировки ПЛ / отправителя) */
  cargoModalReportTabConstraints: CargoModalReportTabConstraints | null
}

type RemoveCargosFromStorePayload = {
  cargoIds: number[]
}

const getCargoClientKey = (cargo: Cargo): string => {
  if (cargo.third_id) {
    return cargo.third_id.toString()
  }
  if (cargo.payer === 0) {
    return cargo.from_id.toString()
  }
  return cargo.to_id.toString()
}

const initialState: InitState = {
  isOnlyCargo: false,
  cargos: null,
  currentCargo: null,
  currentCargoGroup: null,
  selectedCargosForDeletion: [],
  reportCargoDeleteDialog: null,
  reportCargoTabPatch: null,
  isPending: false,
  isPendingGetCargos: false,
  isPendingAddCargos: false,
  isPendingUpdateCargos: false,
  isPendingDeleteCargos: false,
  isPendingDeleteCargosImage: false,
  isPendingDeleteManyCargos: false,
  isVisibleModalCargo: false,
  isVisibleDeleteModalCargo: false,
  isVisibleDeleteManyModalCargo: false,

  isVisibleSideModal: false,
  isVisibleContentDeliveries: false,
  isDisabledEdit: false,
  currentCargoGroupIdClient: null,
  newCargoReportIdPrefill: null,
  cargoModalReportTabConstraints: null,
}

export const fetchCargos = createAsyncThunk(
  'cargos/fetchCargos',
  async (_, thunkAPI) => {
    try {
      const response: AxiosResponse = await axiosInstance.get(URL.cargos)
      if (response.statusText === 'OK') {
        return response.data
      }
      throw new Error()
    } catch (error) {
      console.error('Ошибка при получении данных:', error)
      return thunkAPI.rejectWithValue('Ошибка при получении данных')
    }
  }
)

type AddCargoPayload = {
  currency: any
  clientIdFrom: any
  clientIdTo: any
  payer: any
  reportId: any
  description: any
  volume: any
  weight: any
  amount: any
  hasTn: boolean
  tnNumber: string
  thirdId?: any // Добавляем поле для третьего лица
  photos?: File[] // Новые фотографии
  images_remove?: number[] // ID фотографий для удаления
  paymentMethod?: CargoPaymentMethod
  isPaid?: boolean
  direction?: ReportCargoDirection
  readinessAt?: string
  canBePickedUp?: boolean
}

type DeleteCargoImagePayload = {
  imageId: number
  cargoId?: number
  serviceId?: number
}

export const fetchDeleteCargoImage = createAsyncThunk(
  'cargos/fetchDeleteCargoImage',
  async (
    { imageId, cargoId, serviceId }: DeleteCargoImagePayload,
    thunkAPI
  ) => {
    const {
      cargos: { currentCargo },
    } = thunkAPI.getState() as RootState

    const resolvedCargoId = cargoId ?? serviceId ?? currentCargo?.id

    if (!resolvedCargoId) {
      return thunkAPI.rejectWithValue('Не указан груз для удаления фото')
    }

    try {
      const response = await axiosInstance.delete(
        `${URL.cargos}/${resolvedCargoId}/image/${imageId}`
      )

      if (response.statusText === 'OK' || response.statusText === 'Created') {
        const result = {
          id: imageId,
          currentCargoId: resolvedCargoId,
          responseData: response.data,
        }
        return result
      }
      throw new Error('Не удалось отправить данные')
    } catch (error) {
      console.error('Ошибка при отправке данных:', error)
      return thunkAPI.rejectWithValue('Ошибка при отправке данных')
    }
  }
)

export const fetchAddCargo = createAsyncThunk(
  'cargos/fetchAddCargo',
  async (payload: AddCargoPayload, thunkAPI) => {
    const {
      currency,
      clientIdFrom,
      clientIdTo,
      payer,
      reportId,
      description,
      volume,
      weight,
      amount,
      hasTn,
      tnNumber,
      thirdId,
      photos,
      images_remove,
      paymentMethod,
      isPaid,
      direction,
      readinessAt,
      canBePickedUp,
    } = payload
    const formData = new FormData()

    const {
      cargos: { currentCargo },
    } = thunkAPI.getState() as RootState

    const toIdPositive =
      clientIdTo != null &&
      String(clientIdTo).trim() !== '' &&
      Number(String(clientIdTo).trim()) > 0
        ? String(Math.trunc(Number(String(clientIdTo).trim())))
        : null

    formData.append('report_id', reportId)
    formData.append('from_id', clientIdFrom)
    /** Поле всегда в запросе: id или пустая строка (на бэке часто nullable → null через middleware) */
    formData.append('to_id', toIdPositive ?? '')
    formData.append('payer', String(payer))
    formData.append(
      'description',
      typeof description === 'string' && description.trim()
        ? description.trim()
        : '—'
    )
    formData.append('volume', String(volume))
    formData.append('weight', String(weight))
    formData.append(
      'amount',
      String(
        amount === '' || amount == null || Number.isNaN(Number(amount))
          ? 0
          : amount
      )
    )
    formData.append('currency', currency)
    formData.append('has_tn', hasTn ? '1' : '0')
    formData.append('tn_number', tnNumber)
    if (thirdId) {
      formData.append('third_id', String(thirdId))
    }
    if (paymentMethod) {
      formData.append('payment_method', paymentMethod)
    }
    formData.append('is_paid', isPaid ? '1' : '0')
    if (!currentCargo?.id && direction) {
      formData.append('direction', direction)
    }

    formData.append('readiness_at', formatCargoReadyAtForApi(readinessAt))
    formData.append('can_be_picked_up', canBePickedUp ? '1' : '0')

    const { userId } = getRole()
    if (!currentCargo?.id && userId != null) {
      formData.append('created_by', String(userId))
    }

    // Добавляем новые фотографии
    if (photos && photos.length > 0) {
      photos.forEach((photo, index) => {
        formData.append(`images[${index}]`, photo)
      })
    }

    // Добавляем ID фотографий для удаления
    if (images_remove && images_remove.length > 0) {
      images_remove.forEach((imageId) => {
        formData.append('images_remove[]', String(imageId))
      })
    }

    // Для обновления добавляем метод PUT
    if (currentCargo?.id) {
      formData.append('_method', 'put')
    }

    try {
      const url = currentCargo?.id
        ? `${URL.cargos}/${currentCargo.id}`
        : URL.cargos
      const response = await axiosInstance.post(url, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      if (response.status === 200 || response.status === 201) {
        return response.data
      }
      throw new Error()
    } catch (error) {
      console.error('Ошибка при отправке данных:', error)
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message ||
          'Ошибка при отправке данных груза'
      )
    }
  }
)

export const fetchDeleteCargo = createAsyncThunk<
  { id: number },
  Cargo | undefined
>('cargos/fetchDeleteCargo', async (cargoFromCaller, thunkAPI) => {
  const state = thunkAPI.getState() as RootState
  const { isOnlyCargo } = state.cargos
  const { currentInvoice } = state.invoices

  const currentCargo = cargoFromCaller ?? state.cargos.currentCargo

  if (!currentCargo) {
    return thunkAPI.rejectWithValue('Нет текущего груза для удаления')
  }

  const reportAssignedUserId =
    state.reports.currentReport?.user?.id ?? currentCargo.report?.user?.id

  if (
    !canCurrentUserDeleteCargo(currentCargo, {
      reportAssignedUserId:
        reportAssignedUserId != null ? Number(reportAssignedUserId) : null,
    })
  ) {
    toast.error('Удалить можно только грузы, которые вы добавили')
    return thunkAPI.rejectWithValue('Недостаточно прав на удаление груза')
  }

  try {
    const response = await axiosInstance.delete(
      `${URL.cargos}/${currentCargo.id}`
    )

    if (response.status === 200) {
      // Если груз был в текущем счете - обновляем его
      // Получаем фильтры из sessionStorage и делаем запрос
      const filters = getFiltersFromSessionStorage()

      if (!isOnlyCargo && filters) {
        thunkAPI.dispatch(fetchInvoices(filters))
      }

      if (
        currentCargo.invoice_id &&
        currentInvoice?.id === currentCargo.invoice_id
      ) {
        const updatedInvoice = {
          ...currentInvoice,
          cargos: currentInvoice.cargos.filter((c) => c.id !== currentCargo.id),
          sum: currentInvoice.sum - (currentCargo.amount || 0),
        }
        thunkAPI.dispatch(setIsOnlyCargo(false))

        thunkAPI.dispatch(setCurrentInvoice(updatedInvoice))
      }

      return { id: currentCargo.id }
    }
    throw new Error()
  } catch (error) {
    console.error('Ошибка при удалении данных:', error)
    return thunkAPI.rejectWithValue('Ошибка при удалении данных')
  }
})

export const fetchPatchCargoStatus = createAsyncThunk(
  'cargos/fetchPatchCargoStatus',
  async (
    { cargoId, ...body }: { cargoId: number } & PatchCargoStatusBody,
    thunkAPI
  ) => {
    try {
      const response = await axiosInstance.patch(
        `/${URL.cargos}/${cargoId}/status`,
        body
      )

      if (response.status === 200 || response.status === 204) {
        return response.data as Cargo | undefined
      }
      throw new Error()
    } catch (error) {
      console.error('Ошибка при смене статуса груза:', error)
      const typedError = error as MyKnownError
      return thunkAPI.rejectWithValue(
        typedError?.response?.data?.message || 'Ошибка при смене статуса груза'
      )
    }
  }
)

export const fetchDeleteManyCargos = createAsyncThunk(
  'cargos/fetchDeleteManyCargos',
  async (cargoIds: number[], thunkAPI) => {
    const state = thunkAPI.getState() as RootState
    const { isAdmin, isOwner, isDriver, userId } = getRole()
    if (!(isAdmin || isOwner)) {
      const reportAssignedUserId = state.reports.currentReport?.user?.id
      const deleteCtx = {
        reportAssignedUserId:
          reportAssignedUserId != null ? Number(reportAssignedUserId) : null,
      }
      const flat =
        state.cargos.cargos != null
          ? (Object.values(state.cargos.cargos).flat() as Cargo[])
          : []
      const notDeletable = cargoIds.find((id) => {
        const c = flat.find((x) => x.id === id)
        if (!c) {
          if (
            isDriver &&
            userId != null &&
            deleteCtx.reportAssignedUserId != null &&
            Number(deleteCtx.reportAssignedUserId) === Number(userId)
          ) {
            return false
          }
          return true
        }
        return !canCurrentUserDeleteCargo(c, deleteCtx)
      })
      if (notDeletable != null) {
        toast.error('Удалить можно только грузы, которые вы добавили')
        return thunkAPI.rejectWithValue('Недостаточно прав на удаление грузов')
      }
    }

    try {
      // Создаем FormData с массивом ID для массового удаления
      const formData = new FormData()
      cargoIds.forEach((id, index) => {
        formData.append(`cargos[${index}]`, id.toString())
      })

      const response = await axiosInstance.post(
        `${URL.cargos}/delete`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      )

      if (response.status === 200 || response.status === 201) {
        // const filters = getFiltersFromSessionStorage()
        // thunkAPI.dispatch(fetchInvoices(filters))

        return { ids: cargoIds }
      }

      throw new Error('Не удалось удалить грузы')
    } catch (error) {
      console.error('Ошибка при массовом удалении данных:', error)
      return thunkAPI.rejectWithValue('Ошибка при массовом удалении данных')
    }
  }
)

export const cargosSlice = createSlice({
  name: 'cargos',
  initialState,
  reducers: {
    setCurrentCargo: (state, action: PayloadAction<Cargo | null>) => {
      state.currentCargo = action.payload
      if (action.payload) {
        state.newCargoReportIdPrefill = null
      }
    },
    setCargoModalReportTabConstraints: (
      state,
      action: PayloadAction<CargoModalReportTabConstraints | null>
    ) => {
      state.cargoModalReportTabConstraints = action.payload
    },
    setNewCargoReportIdPrefill: (
      state,
      action: PayloadAction<number | null>
    ) => {
      state.newCargoReportIdPrefill = action.payload
    },
    setCargosGroup: (state, action: PayloadAction<Cargo[] | null>) => {
      state.currentCargoGroup = action.payload
    },
    setVisibleSideModal: (state, action) => {
      state.isVisibleSideModal = action.payload
    },
    setVisibleContentDeliveries: (state, action) => {
      state.isVisibleContentDeliveries = action.payload
    },

    setIsVisibleModalCargo: (state, action: PayloadAction<boolean>) => {
      state.isVisibleModalCargo = action.payload
      if (!action.payload) {
        state.cargoModalReportTabConstraints = null
      }
    },

    setIsOnlyCargo: (state, action: { payload: boolean }) => ({
      ...state,
      isOnlyCargo: action.payload,
    }),

    setIsVisibleDeleteModalCargo: (state, action: { payload: boolean }) => ({
      ...state,
      isVisibleDeleteModalCargo: action.payload,
    }),
    setIsVisibleDeleteManyModalCargo: (
      state,
      action: { payload: boolean }
    ) => ({
      ...state,
      isVisibleDeleteManyModalCargo: action.payload,
    }),

    setIsDisabledEdit: (state, action) => {
      state.isDisabledEdit = action.payload
    },

    setCurrentCargoGroupIdClient: (state, action) => {
      state.currentCargoGroupIdClient = action.payload
    },
    setSelectedCargosForDeletion: (state, action: PayloadAction<number[]>) => {
      state.selectedCargosForDeletion = action.payload
    },
    removeCargosFromStore: (
      state,
      action: PayloadAction<RemoveCargosFromStorePayload>
    ) => {
      const { cargoIds } = action.payload
      if (!cargoIds.length) return

      if (state.cargos) {
        const newCargos = { ...state.cargos }
        for (const key in newCargos) {
          newCargos[key] = newCargos[key].filter(
            (item) => !cargoIds.includes(item.id)
          )
        }
        state.cargos = newCargos
      }

      if (state.currentCargoGroup) {
        state.currentCargoGroup = state.currentCargoGroup.filter(
          (item) => !cargoIds.includes(item.id)
        )
      }

      if (state.currentCargo && cargoIds.includes(state.currentCargo.id)) {
        state.currentCargo = null
      }
    },
    setReportCargoDeleteDialog: (
      state,
      action: PayloadAction<ReportCargoDeleteDialog | null>
    ) => {
      state.reportCargoDeleteDialog = action.payload
    },
    setReportCargoTabPatch: (
      state,
      action: PayloadAction<ReportCargoTabPatch>
    ) => {
      state.reportCargoTabPatch = action.payload
    },
    clearReportCargoTabPatch: (state) => {
      state.reportCargoTabPatch = null
    },
    returnCargoToStore: (state, action: PayloadAction<Cargo>) => {
      const cargoFromInvoice = action.payload
      const cargo: Cargo = { ...cargoFromInvoice, invoice_id: null }

      if (state.cargos) {
        const newCargos = { ...state.cargos }

        // На всякий случай удаляем груз из всех групп перед добавлением
        for (const key in newCargos) {
          newCargos[key] = newCargos[key].filter((item) => item.id !== cargo.id)
        }

        const clientKey = getCargoClientKey(cargo)
        if (!newCargos[clientKey]) {
          newCargos[clientKey] = []
        }
        newCargos[clientKey].unshift(cargo)
        state.cargos = newCargos
      } else {
        state.cargos = { [getCargoClientKey(cargo)]: [cargo] }
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchCargos.pending, (state) => {
        state.isPendingGetCargos = true
      })
      .addCase(
        fetchCargos.fulfilled,
        (state, action: PayloadAction<CargosResponse>) => {
          state.cargos = action.payload
          state.isPendingGetCargos = false
        }
      )
      .addCase(fetchCargos.rejected, (state, action) => {
        toast.error(action.payload as string)
        state.isPendingGetCargos = false
      })

    builder
      .addCase(fetchAddCargo.pending, (state) => {
        state.isPendingAddCargos = true
      })
      .addCase(
        fetchAddCargo.fulfilled,
        (state, action: PayloadAction<Cargo>) => {
          toast.success('Сохранено!')
          const cargo = action.payload

          // Обработка основного объекта cargos
          if (state.cargos) {
            let updated = false
            const newCargos = { ...state.cargos }
            for (const key in newCargos) {
              const index = newCargos[key].findIndex((c) => c.id === cargo.id)
              if (index !== -1) {
                // Если груз уже в списке
                if (cargo.invoice_id !== null) {
                  // Груз привязан к счету - удаляем из списка "К оплате"
                  newCargos[key] = newCargos[key].filter(
                    (c) => c.id !== cargo.id
                  )
                } else {
                  // Груз не привязан к счету - обновляем
                  newCargos[key][index] = cargo
                }
                updated = true
                break
              }
            }
            if (!updated && cargo.invoice_id === null) {
              // Добавляем только грузы, которые НЕ привязаны к счету
              // Определяем правильный ключ на основе плательщика
              let clientKey: string

              if (cargo.third_id) {
                // Если есть третье лицо - оно является основным клиентом
                clientKey = cargo.third_id.toString()
              } else if (cargo.payer === 0) {
                // Плательщик - отправитель
                clientKey = cargo.from_id.toString()
              } else {
                // Плательщик - получатель
                clientKey = cargo.to_id.toString()
              }

              if (newCargos[clientKey]) {
                newCargos[clientKey].unshift(cargo)
              } else {
                newCargos[clientKey] = [cargo]
              }
            }
            state.cargos = newCargos
          } else if (cargo.invoice_id === null) {
            // Создаем список только если груз не привязан к счету
            // Определяем правильный ключ на основе плательщика
            let clientKey: string

            if (cargo.third_id) {
              // Если есть третье лицо - оно является основным клиентом
              clientKey = cargo.third_id.toString()
            } else if (cargo.payer === 0) {
              // Плательщик - отправитель
              clientKey = cargo.from_id.toString()
            } else {
              // Плательщик - получатель
              clientKey = cargo.to_id.toString()
            }

            state.cargos = { [clientKey]: [cargo] }
          }

          // Обработка currentCargoGroup с учетом изменения плательщика
          // Обработка currentCargoGroup с учетом изменения плательщика
          if (state.currentCargoGroup) {
            const index = state.currentCargoGroup.findIndex(
              (c) => c.id === cargo.id
            )

            if (index !== -1) {
              // Если груз уже в группе - просто обновляем его
              state.currentCargoGroup[index] = cargo
            } else {
              // Проверяем, принадлежит ли новый/обновленный груз к текущей группе
              const firstCargoInGroup = state.currentCargoGroup[0]
              if (firstCargoInGroup) {
                // Определяем плательщика для группы
                const groupPayerId =
                  firstCargoInGroup.payer === 0
                    ? firstCargoInGroup.from_id
                    : firstCargoInGroup.to_id

                // Определяем плательщика для нового груза
                const cargoPayerId =
                  cargo.payer === 0 ? cargo.from_id : cargo.to_id

                // Если плательщик тот же - добавляем в группу
                if (groupPayerId === cargoPayerId) {
                  state.currentCargoGroup.unshift(cargo)
                }
              }
            }
          }

          state.currentCargo = cargo
          state.isPendingAddCargos = false
        }
      )
      .addCase(fetchAddCargo.rejected, (state, action) => {
        toast.error(action.payload as string)
        state.isPendingAddCargos = false
      })

    builder
      .addCase(fetchDeleteCargo.pending, (state) => {
        state.isPendingDeleteCargos = true
      })
      .addCase(
        fetchDeleteCargo.fulfilled,
        (state, action: PayloadAction<{ id: number }>) => {
          toast.success('Груз удалён!')
          if (state.cargos) {
            const newCargos = { ...state.cargos }

            // Удаляем груз из всех категорий
            for (const key in newCargos) {
              newCargos[key] = newCargos[key].filter(
                (item) => item.id !== action.payload.id
              )
            }
            if (state.currentCargoGroup) {
              state.currentCargoGroup = state.currentCargoGroup.filter(
                (item) => item.id !== action.payload.id
              )
            }
            state.cargos = newCargos
          }
          state.currentCargo = null
          state.isPendingDeleteCargos = false
          state.isVisibleDeleteModalCargo = false
        }
      )
      .addCase(fetchDeleteCargo.rejected, (state, action) => {
        toast.error(action.payload as string)
        state.isPendingDeleteCargos = false
        state.isVisibleDeleteModalCargo = false
      })

    builder
      .addCase(fetchDeleteManyCargos.pending, (state) => {
        state.isPendingDeleteManyCargos = true
      })
      .addCase(
        fetchDeleteManyCargos.fulfilled,
        (state, action: PayloadAction<{ ids: number[] }>) => {
          toast.success('Выбранные грузы удалены!')
          if (state.cargos) {
            const newCargos = { ...state.cargos }

            // Удаляем грузы из всех категорий
            for (const key in newCargos) {
              newCargos[key] = newCargos[key].filter(
                (item) => !action.payload.ids.includes(item.id)
              )
            }
            if (state.currentCargoGroup) {
              state.currentCargoGroup = state.currentCargoGroup.filter(
                (item) => !action.payload.ids.includes(item.id)
              )
            }
            state.cargos = newCargos
          }
          state.currentCargo = null
          state.isPendingDeleteManyCargos = false
          state.isVisibleDeleteManyModalCargo = false
        }
      )
      .addCase(fetchDeleteManyCargos.rejected, (state, action) => {
        toast.error(action.payload as string)
        state.isPendingDeleteManyCargos = false
        state.isVisibleDeleteManyModalCargo = false
      })

    builder
      .addCase(fetchDeleteCargoImage.pending, (state) => {
        state.isPendingDeleteCargosImage = true
      })
      .addCase(
        fetchDeleteCargoImage.fulfilled,
        (
          state,
          action: PayloadAction<{
            id: number
            currentCargoId: number
            responseData: any
          }>
        ) => {
          toast.success('Картинка удалена!')

          if (
            state.currentCargo &&
            state.currentCargo.id === action.payload.currentCargoId
          ) {
            state.currentCargo = {
              ...state.currentCargo,
              images:
                state.currentCargo.images?.filter(
                  (img) => img.id !== action.payload.id
                ) || [],
            }
          }

          // Также обновляем груз в currentCargoGroup если он там есть
          if (state.currentCargoGroup) {
            const cargoIndex = state.currentCargoGroup.findIndex(
              (cargo) => cargo.id === action.payload.currentCargoId
            )

            if (cargoIndex !== -1) {
              state.currentCargoGroup[cargoIndex] = {
                ...state.currentCargoGroup[cargoIndex],
                images:
                  state.currentCargoGroup[cargoIndex].images?.filter(
                    (img) => img.id !== action.payload.id
                  ) || [],
              }
            }
          }

          // Обновляем груз в основном массиве cargos
          if (state.cargos) {
            const newCargos = { ...state.cargos }

            for (const key in newCargos) {
              const cargoIndex = newCargos[key].findIndex(
                (cargo) => cargo.id === action.payload.currentCargoId
              )

              if (cargoIndex !== -1) {
                newCargos[key][cargoIndex] = {
                  ...newCargos[key][cargoIndex],
                  images:
                    newCargos[key][cargoIndex].images?.filter(
                      (img) => img.id !== action.payload.id
                    ) || [],
                }
                break
              }
            }

            state.cargos = newCargos
          }

          state.isPendingDeleteCargosImage = false
        }
      )
      .addCase(fetchDeleteCargoImage.rejected, (state, action) => {
        toast.error(action.payload as string)
        state.isPendingDeleteCargosImage = false
      })

    builder.addCase(fetchPatchCargoStatus.rejected, (_, action) => {
      toast.error(action.payload as string)
    })
  },
})

export const submitReportCargoDeleteFromDialog = createAsyncThunk(
  'cargos/submitReportCargoDeleteFromDialog',
  async (_, { getState, dispatch, rejectWithValue }) => {
    const state = getState() as RootState
    const dialog = state.cargos.reportCargoDeleteDialog
    if (!dialog) return

    dispatch(cargosSlice.actions.setReportCargoDeleteDialog(null))

    const reportIdRaw =
      state.reports.currentReport?.id ?? state.reports.currentReportId
    const reportId = Number(reportIdRaw)

    const refreshReportAndGroupedCargos = async (
      groupedDirection?: ReportCargoDirection
    ) => {
      if (!Number.isFinite(reportId) || reportId <= 0) return
      try {
        await dispatch(fetchCurrentReport(reportId)).unwrap()
      } catch {
        /* шапка путевого могла не обновиться; всё равно подтягиваем grouped */
      }
      if (groupedDirection === 'forward' || groupedDirection === 'backward') {
        void dispatch(
          fetchReportGroupedCargos({
            reportId,
            direction: groupedDirection,
          })
        )
      } else {
        void dispatch(fetchAllReportGroupedCargos(reportId))
      }
    }

    if (dialog.type === 'shipment') {
      if (dialog.cargo) {
        const result = await dispatch(fetchDeleteCargo(dialog.cargo))
        if (fetchDeleteCargo.rejected.match(result)) {
          return rejectWithValue(result.payload)
        }
        dispatch(
          cargosSlice.actions.setReportCargoTabPatch({
            kind: 'removeCargo',
            cargoId: dialog.cargo.id,
          })
        )
        await refreshReportAndGroupedCargos(dialog.direction)
      }
      return
    }

    const cargoIds = dialog.cargoIds.filter(
      (id) => Number.isFinite(id) && id > 0
    )
    /** Черновик клиента без грузов на бэкенде — удаляем только карточку в UI */
    if (!cargoIds.length) {
      dispatch(
        cargosSlice.actions.setReportCargoTabPatch({
          kind: 'removeDelivery',
          senderRowKey: dialog.senderRowKey,
        })
      )
      return
    }

    const result = await dispatch(fetchDeleteManyCargos(cargoIds))
    if (fetchDeleteManyCargos.rejected.match(result)) {
      return rejectWithValue(result.payload)
    }

    dispatch(
      cargosSlice.actions.setReportCargoTabPatch({
        kind: 'removeDelivery',
        senderRowKey: dialog.senderRowKey,
      })
    )

    await refreshReportAndGroupedCargos(dialog.direction)
  }
)

export const {
  setCargosGroup,
  setVisibleContentDeliveries,
  setCurrentCargo,
  setNewCargoReportIdPrefill,
  setCargoModalReportTabConstraints,
  setIsVisibleModalCargo,
  setIsVisibleDeleteModalCargo,
  setIsVisibleDeleteManyModalCargo,
  setVisibleSideModal,
  setIsDisabledEdit,
  setCurrentCargoGroupIdClient,
  setSelectedCargosForDeletion,
  setIsOnlyCargo,
  removeCargosFromStore,
  returnCargoToStore,
  setReportCargoDeleteDialog,
  clearReportCargoTabPatch,
} = cargosSlice.actions

export default cargosSlice.reducer
